import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { catchError, EMPTY, filter, switchMap, take, tap } from 'rxjs';
import { permissionLabel } from '../../../../models/permission.model';
import { TagResponse, TagService } from '../../../../services/tag.service';

import { TagFilterComponent } from '../../../tag-filter/tag-filter.component';
import { InteractionEditorStateService } from '../../services/interaction-editor-state.service';

@Component({
  selector: 'app-tag-panel',
  imports: [MatIcon, MatIconButton, MatTooltip, TagFilterComponent],
  templateUrl: './tag-panel.component.html',
  styleUrl: './tag-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagPanelComponent {
  private readonly _tagService = inject(TagService);
  private readonly _editorState = inject(InteractionEditorStateService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _permissionService = inject(PermissionService);

  /**
   * The interaction's own tags come with `interaction:read`, but the list to
   * pick new ones from is `/tags`, which needs `tag:read`. Without it the
   * assigned tags still show and the picker makes way for a line saying why.
   */
  protected readonly canReadTags = computed(() =>
    this._permissionService.hasPermission('tag:read')
  );
  protected readonly tagReadLabel = permissionLabel('tag:read');

  /** Whether tag controls should be disabled (published interaction). */
  readonly disabled = input(false);

  /** All available tags (from settings). */
  protected readonly allTags = this._tagService.tags;

  /** Tags assigned to the current interaction. */
  readonly assignedTags = signal<TagResponse[]>([]);

  /** Tags not yet assigned (available for adding). */
  protected readonly availableTags = computed(() => {
    const assignedIds = new Set(this.assignedTags().map((t) => t.id));
    return this.allTags().filter((t) => !assignedIds.has(t.id));
  });

  /** IDs of currently assigned tags — bridges TagResponse[] to TagFilterComponent's string[] model. */
  readonly assignedTagIds = computed(() =>
    this.assignedTags().map((t) => t.id)
  );

  /** Tag IDs as they were when the editor opened (= published version's tags). */
  readonly initialTagIds = signal<string[]>([]);

  /** Active assigned tags — can be removed. */
  protected readonly activeAssignedTags = computed(() =>
    this.assignedTags()
      .filter((t) => t.status !== 'inactive')
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  /** Inactive assigned tags — displayed as frozen/muted chips. */
  protected readonly inactiveAssignedTags = computed(() =>
    this.assignedTags()
      .filter((t) => t.status === 'inactive')
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  private readonly _interactionId = computed(
    () => this._editorState.interaction()?._id ?? null
  );

  constructor() {
    // Load available tags, once `tag:read` is known to be held: permissions
    // arrive with the user fetch, which `authGuard` does not await.
    toObservable(this.canReadTags)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this._tagService.getAll())
      )
      .subscribe();

    // Reload assigned tags whenever the interaction changes
    toObservable(this._interactionId)
      .pipe(
        filter((id): id is string => id != null),
        switchMap((id) =>
          this._tagService.getInteractionTags(id).pipe(catchError(() => EMPTY))
        ),
        tap((tags) => {
          this.assignedTags.set(tags);
          this.initialTagIds.set(tags.map((t) => t.id));
        })
      )
      .subscribe();
  }

  addTag(tag: TagResponse) {
    const interactionId = this._interactionId();
    if (!interactionId) return;

    const updated = [...this.assignedTags(), tag];
    this.assignedTags.set(updated);

    this._tagService
      .updateInteractionTags(
        interactionId,
        updated.map((t) => t.id)
      )
      .pipe(
        catchError(() => {
          // Revert optimistic update
          this.assignedTags.update((tags) => tags.filter((t) => t.id !== tag.id));
          this._snackBar.open('Tag toewijzen mislukt', '', { duration: 2000 });
          return EMPTY;
        })
      )
      .subscribe();
  }

  removeTag(tag: TagResponse) {
    const interactionId = this._interactionId();
    if (!interactionId) return;

    const updated = this.assignedTags().filter((t) => t.id !== tag.id);
    this.assignedTags.set(updated);

    this._tagService
      .updateInteractionTags(
        interactionId,
        updated.map((t) => t.id)
      )
      .pipe(
        catchError(() => {
          // Revert optimistic update
          this.assignedTags.update((tags) => [...tags, tag]);
          this._snackBar.open('Tag verwijderen mislukt', '', {
            duration: 2000,
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Called when the TagFilterComponent emits a new full selection.
   * Syncs the assigned tags in a single API call instead of one per add/remove.
   * Preserves inactive tag IDs — they can't be changed via the filter.
   */
  handleTagSelectionChange(ids: string[]): void {
    const interactionId = this._interactionId();
    if (!interactionId) return;

    // Preserve inactive tag IDs — they can't be changed via the filter
    const inactiveIds = this.inactiveAssignedTags().map((t) => t.id);
    const mergedIds = [...new Set([...inactiveIds, ...ids])];

    const idSet = new Set(mergedIds);
    const newAssigned = [
      ...this.allTags().filter((t) => idSet.has(t.id)),
      ...this.inactiveAssignedTags(),
    ];
    const previousAssigned = this.assignedTags();

    // Optimistic update
    this.assignedTags.set(newAssigned);

    this._tagService
      .updateInteractionTags(interactionId, mergedIds)
      .pipe(
        catchError(() => {
          // Revert on error
          this.assignedTags.set(previousAssigned);
          this._snackBar.open('Tags bijwerken mislukt', '', { duration: 2000 });
          return EMPTY;
        })
      )
      .subscribe();
  }
}
